import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { OficinaFormDialog } from "@/components/forms/OficinaFormDialog";
import { AlunoFormDialog } from "@/components/forms/AlunoFormDialog";
import { InscricaoManager } from "@/components/InscricaoManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  FolderKanban, GraduationCap, Dumbbell, Users, Check, ArrowRight, Loader2, BarChart3, Plus, Settings2, ShieldAlert, ChevronsUpDown, UserPlus, Search, ClipboardCheck, History, UserCog, X, Clock,
} from "lucide-react";
import { toast } from "sonner";

const displayInstrutor = (u) => {
  const grau = (u.grau_nome || "").trim();
  const nome = (u.nome_guerra || "").trim() || (u.name || "").trim();
  return grau && grau.toLowerCase() !== "não se aplica" ? `${grau} ${nome}`.trim() : nome;
};

// Wizard: Curso -> Turma -> Oficina -> Aluno -> cria avaliação
export default function Avaliar() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [step, setStep] = useState(0);
  const [cursos, setCursos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [instrutores, setInstrutores] = useState([]);
  const [respOpen, setRespOpen] = useState(false);
  const [sel, setSel] = useState({ curso: null, turma: null, oficina: null, aluno: null });
  const [creating, setCreating] = useState(false);
  const [novaTurma, setNovaTurma] = useState(false);
  const [novaOficina, setNovaOficina] = useState(false);
  const [novoAlunoOpen, setNovoAlunoOpen] = useState(false);
  const [existOpen, setExistOpen] = useState(false);
  const [todos, setTodos] = useState([]);
  const [busca, setBusca] = useState("");
  const [existSel, setExistSel] = useState([]);
  const emptyTForm = () => ({ nome: "", instituicao: "", unidade: "", local: "", responsavel: user?.role === "instrutor" ? displayInstrutor(user) : "", responsavel_id: user?.role === "instrutor" ? user.id : "", data_inicio: "", observacoes: "", status: "ativa" });
  const [tForm, setTForm] = useState(emptyTForm());
  const [savingT, setSavingT] = useState(false);
  const [modo, setModo] = useState(() => (new URLSearchParams(window.location.search).get("modo") === "continuar" ? "continuar" : "nova"));
  const [emAndamento, setEmAndamento] = useState([]);
  const [loadingCont, setLoadingCont] = useState(false);
  const [equipeOpen, setEquipeOpen] = useState(false);
  const [eqInstr, setEqInstr] = useState([]);
  const [eqMon, setEqMon] = useState([]);
  const [savingEq, setSavingEq] = useState(false);

  useEffect(() => {
    if (modo !== "continuar") return;
    setLoadingCont(true);
    api.get("/avaliacoes?status=em_andamento").then((r) => setEmAndamento(r.data || [])).catch(() => setEmAndamento([])).finally(() => setLoadingCont(false));
  }, [modo]);

  useEffect(() => {
    api.get("/cursos?apenas_ativos=true").then((r) => setCursos(r.data));
    api.get("/auth/instrutores/ativos").then((r) => setInstrutores(r.data)).catch(() => setInstrutores([]));
  }, []);

  const podeCriar = (c) => isAdmin || (c && c.permite_aplicacoes_instrutor);

  const loadTurmas = (cursoId) => api.get(`/turmas?curso_id=${cursoId}`).then((r) => setTurmas(r.data));
  const loadOficinas = (cursoId) => api.get(`/oficinas?curso_id=${cursoId}`).then((r) => setOficinas(r.data));

  const pickCurso = (c) => {
    setSel({ curso: c, turma: null, oficina: null, aluno: null });
    Promise.all([loadTurmas(c.id), loadOficinas(c.id)]).then(() => setStep(1));
  };
  const pickTurma = (t) => {
    setSel((s) => ({ ...s, turma: t, aluno: null }));
    api.get(`/alunos?turma_id=${t.id}`).then((r) => { setAlunos(r.data); setStep(2); });
  };
  const pickOficina = (o) => { setSel((s) => ({ ...s, oficina: o })); setStep(3); };

  const criarTurma = async () => {
    if (!tForm.nome.trim()) { toast.error("Informe o nome da aplicação"); return; }
    setSavingT(true);
    try {
      const { data } = await api.post("/turmas", { ...tForm, curso_id: sel.curso.id });
      toast.success("Aplicação criada");
      setNovaTurma(false);
      setTForm(emptyTForm());
      await loadTurmas(sel.curso.id);
      pickTurma(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingT(false); }
  };

  const start = async (aluno) => {
    setCreating(true);
    try {
      const { data } = await api.post("/avaliacoes", {
        curso_id: sel.curso.id, turma_id: sel.turma.id,
        oficina_id: sel.oficina.id, aluno_id: aluno.id,
      });
      const back = `/aplicacao/${sel.turma.id}/${sel.oficina.id}`;
      navigate(`/avaliar/${data.id}?back=${encodeURIComponent(back)}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setCreating(false); }
  };

  const steps = ["Curso", "Turma", "Oficina", "Aluno"];

  const tFormDirty = Object.entries(tForm).some(([k, v]) => !["status", "responsavel", "responsavel_id"].includes(k) && v);

  const reloadAlunos = () => { if (sel.turma) api.get(`/alunos?turma_id=${sel.turma.id}`).then((r) => setAlunos(r.data)); };
  const jaNaTurma = (a) => a.turma_id === sel.turma?.id || (a.turma_ids || []).includes(sel.turma?.id);
  const openExistentes = async () => {
    setExistSel([]); setBusca(""); setExistOpen(true);
    const { data } = await api.get("/alunos");
    setTodos(data);
  };
  const toggleExist = (aid) => setExistSel((s) => s.includes(aid) ? s.filter((x) => x !== aid) : [...s, aid]);
  const vincularExistentes = async () => {
    if (!sel.turma) return;
    let ok = 0;
    for (const aid of existSel) {
      try { await api.post(`/turmas/${sel.turma.id}/alunos/${aid}`); ok += 1; } catch (e) { /* já vinculado */ }
    }
    toast.success(`${ok} aluno(s) adicionado(s) à turma`);
    setExistOpen(false); setExistSel([]);
    reloadAlunos();
  };
  const buscaLc = busca.toLowerCase();
  const filtradosExist = todos.filter((a) => !buscaLc || [a.nome, a.nome_guerra, a.rg, a.matricula, a.identificacao_funcional, a.instituicao_nome, a.unidade_nome].some((f) => (f || "").toLowerCase().includes(buscaLc)));
  const closeNovaTurma = (v) => {
    if (!v && tFormDirty && !window.confirm("Deseja sair sem salvar? As alterações não salvas serão perdidas.")) return;
    setNovaTurma(v);
  };

  const abrirEquipe = () => { setEqInstr(sel.turma?.instrutor_ids || []); setEqMon(sel.turma?.monitores || []); setEquipeOpen(true); };
  const toggleEqInstr = (iid) => setEqInstr((s) => s.includes(iid) ? s.filter((x) => x !== iid) : [...s, iid]);
  const salvarEquipe = async () => {
    if (!sel.turma) return;
    setSavingEq(true);
    try {
      const mon = eqMon.map((m) => (m || "").trim()).filter(Boolean);
      await api.put(`/turmas/${sel.turma.id}/equipe`, { instrutor_ids: Array.from(new Set(eqInstr)), monitores: mon });
      setSel((s) => ({ ...s, turma: { ...s.turma, instrutor_ids: Array.from(new Set(eqInstr)), monitores: mon } }));
      toast.success("Equipe da turma atualizada");
      setEquipeOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingEq(false); }
  };

  const resumirAvaliacao = (av) => {
    const back = `/aplicacao/${av.turma_id}/${av.oficina_id}`;
    navigate(`/avaliar/${av.id}?back=${encodeURIComponent(back)}`);
  };

  return (
    <div>
      <button data-testid="voltar-btn" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <span aria-hidden>←</span> Voltar
      </button>
      <PageHeader title="Avaliar" subtitle="Organize e execute a avaliação sem sair desta tela" />

      <div className="grid grid-cols-2 gap-2 mb-6">
        <button data-testid="modo-nova" onClick={() => setModo("nova")}
          className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-all ${modo === "nova" ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border hover:bg-secondary/50"}`}>
          <ClipboardCheck className={`h-5 w-5 shrink-0 ${modo === "nova" ? "text-primary" : "text-muted-foreground"}`} />
          <div className="min-w-0"><div className="font-semibold text-sm">Nova avaliação</div><div className="text-xs text-muted-foreground">Criar ou selecionar a atividade</div></div>
        </button>
        <button data-testid="modo-continuar" onClick={() => setModo("continuar")}
          className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-all ${modo === "continuar" ? "border-sky-500 bg-sky-500/10 ring-1 ring-sky-500/40" : "border-border hover:bg-secondary/50"}`}>
          <History className={`h-5 w-5 shrink-0 ${modo === "continuar" ? "text-sky-400" : "text-muted-foreground"}`} />
          <div className="min-w-0"><div className="font-semibold text-sm">Continuar avaliação</div><div className="text-xs text-muted-foreground">Retomar uma em andamento</div></div>
        </button>
      </div>

      {modo === "continuar" && (
        <div data-testid="continuar-panel">
          {loadingCont ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Carregando avaliações em andamento…</div>
          ) : !emAndamento.length ? (
            <div className="rounded-lg border border-border bg-card p-6 text-center">
              <Clock className="h-9 w-9 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground text-sm">Nenhuma avaliação em andamento. Use <strong>Nova avaliação</strong> para iniciar.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {emAndamento.map((av) => (
                <button key={av.id} data-testid={`continuar-${av.id}`} onClick={() => resumirAvaliacao(av)}
                  className="flex items-center gap-3 p-4 rounded-lg border border-sky-500/40 bg-sky-500/5 hover:bg-sky-500/10 transition-all text-left active:scale-[0.98]">
                  <div className="p-2 rounded-full bg-sky-500/15 shrink-0"><Clock className="h-5 w-5 text-sky-400" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{av.aluno_nome}{av.aluno_nome_guerra ? <span className="text-xs text-primary font-mono ml-1">"{av.aluno_nome_guerra}"</span> : ""}</div>
                    <div className="text-xs text-muted-foreground truncate">{[av.turma_nome, av.oficina_nome].filter(Boolean).join(" · ")}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 ml-auto text-sky-400 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {modo === "nova" && (
      <div className="flex items-center flex-wrap gap-2 mb-6 text-sm font-mono">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <button
              data-testid={`step-${s.toLowerCase()}`}
              disabled={i > step}
              onClick={() => i < step && setStep(i)}
              className={`px-3 py-1.5 rounded-md border transition-colors max-w-full whitespace-normal break-words text-left ${
                i === step ? "bg-primary text-primary-foreground border-primary"
                : i < step ? "bg-secondary text-foreground border-border hover:bg-accent"
                : "text-muted-foreground border-border opacity-50"
              }`}
            >
              {i + 1}. {s}
              {i === 0 && sel.curso ? `: ${sel.curso.nome}` : ""}
              {i === 1 && sel.turma ? `: ${sel.turma.nome}` : ""}
              {i === 2 && sel.oficina ? `: ${sel.oficina.nome}` : ""}
            </button>
            {i < steps.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
        ))}
      </div>
      )}

      {modo === "nova" && step === 0 && (
        <div className="space-y-8">
          <div>
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-sky-400 mb-3 flex items-center gap-2"><FolderKanban className="h-4 w-4" />Cursos</h3>
            <SelGrid items={cursos.filter((c) => (c.tipo || "curso") === "curso" && c.banco_criterios !== "primeiro_interventor" && !/primeiro\s+interventor/i.test(c.nome || ""))} icon={FolderKanban} color="text-sky-400" onPick={pickCurso} testid="curso" empty="Nenhum curso disponível." />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-amber-400 mb-3 flex items-center gap-2"><ShieldAlert className="h-4 w-4" />Instruções</h3>
            <SelGrid items={cursos.filter((c) => c.tipo === "instrucao")} icon={ShieldAlert} color="text-amber-400" onPick={pickCurso} testid="instrucao" empty="Nenhuma instrução disponível." />
          </div>
        </div>
      )}

      {modo === "nova" && step === 1 && (
        <div>
          {podeCriar(sel.curso) && (
            <div className="mb-4">
              <Button data-testid="nova-turma-wizard-btn" onClick={() => setNovaTurma(true)}>
                <Plus className="h-4 w-4 mr-2" /> Nova turma / aplicação
              </Button>
            </div>
          )}
          <SelGrid items={turmas} icon={GraduationCap} color="text-purple-400" onPick={pickTurma} testid="turma"
            empty={podeCriar(sel.curso) ? "Nenhuma turma ainda. Crie a sua aplicação acima." : "Nenhuma turma neste curso."} />
        </div>
      )}

      {modo === "nova" && step === 2 && (
        <div>
          {podeCriar(sel.curso) && (
            <div className="mb-4">
              <Button variant="secondary" data-testid="nova-oficina-wizard-btn" onClick={() => setNovaOficina(true)}>
                <Settings2 className="h-4 w-4 mr-2" /> Nova oficina
              </Button>
            </div>
          )}
          {!oficinas.length ? <p className="text-muted-foreground text-sm">Nenhuma oficina neste curso. {podeCriar(sel.curso) && "Crie uma oficina ou use uma existente para continuar."}</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {oficinas.map((o) => (
                <div key={o.id} className="rounded-lg border border-border bg-card overflow-hidden">
                  <button data-testid={`pick-oficina-${o.id}`} onClick={() => pickOficina(o)}
                    className="w-full flex items-center gap-3 p-4 hover:bg-card/70 transition-all text-left active:scale-[0.98]">
                    <div className="p-2 rounded-lg bg-secondary"><Dumbbell className="h-5 w-5 text-emerald-400" /></div>
                    <div className="min-w-0"><div className="font-semibold truncate">{o.nome}</div>
                      <div className="text-xs text-muted-foreground">{(o.criterios || []).length} critérios</div></div>
                    <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </button>
                  <button data-testid={`consolidado-${o.id}`} onClick={() => navigate(`/pratica/consolidado/${sel.turma.id}/${o.id}`)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 border-t border-border text-xs text-primary hover:bg-secondary/60 transition-colors">
                    <BarChart3 className="h-4 w-4" /> Ver consolidado da turma
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {modo === "nova" && step === 3 && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight" data-testid="wizard-alunos-count">Alunos ({alunos.length})</h3>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" data-testid="wizard-cadastrar-aluno" onClick={() => setNovoAlunoOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Novo aluno</Button>
              <Button size="sm" variant="secondary" data-testid="wizard-aluno-existente" onClick={openExistentes}><UserPlus className="h-4 w-4 mr-1.5" /> Adicionar aluno existente</Button>
              <Button size="sm" variant="secondary" data-testid="wizard-add-instrutor" onClick={abrirEquipe}><UserCog className="h-4 w-4 mr-1.5" /> Adicionar instrutor</Button>
              <Button size="sm" variant="secondary" data-testid="wizard-add-monitor" onClick={abrirEquipe}><Users className="h-4 w-4 mr-1.5" /> Adicionar monitor</Button>
            </div>
          </div>
          {sel.turma && (
            <div className="mb-4" data-testid="wizard-inscricao-manager">
              <InscricaoManager turma={sel.turma} linkLabel="Gerar link / QR Code" />
            </div>
          )}
          {!alunos.length ? (
            <div className="rounded-lg border border-border bg-card p-6 text-center">
              <Users className="h-9 w-9 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground text-sm">Nenhum aluno nesta turma ainda. Cadastre um novo aluno, adicione um existente ou gere o link/QR Code — você permanece nesta tela.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {alunos.map((a) => (
                <button key={a.id} data-testid={`pick-aluno-${a.id}`} disabled={creating} onClick={() => start(a)}
                  className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary hover:bg-card/70 transition-all text-left active:scale-[0.98]">
                  <div className="p-2 rounded-full bg-amber-500/15"><Users className="h-5 w-5 text-amber-400" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{a.nome}</div>
                    <div className="text-xs text-muted-foreground font-mono">{a.identificacao_funcional || a.matricula || "—"}</div>
                  </div>
                  {creating ? <Loader2 className="h-4 w-4 ml-auto animate-spin" /> : <Check className="h-4 w-4 ml-auto text-muted-foreground" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <OficinaFormDialog open={novaOficina} onOpenChange={setNovaOficina} lockCursoId={sel.curso?.id}
        onSaved={() => sel.curso && loadOficinas(sel.curso.id)} />

      <AlunoFormDialog open={novoAlunoOpen} onOpenChange={setNovoAlunoOpen} lockTurmaId={sel.turma?.id}
        onSaved={reloadAlunos} />

      <Dialog open={equipeOpen} onOpenChange={setEquipeOpen}>
        <DialogContent className="bg-popover border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Instrutores e monitores da turma</DialogTitle>
            <DialogDescription>Vincule instrutores já cadastrados e informe monitores (texto livre) sem sair do fluxo de avaliação. Os vínculos existentes são preservados.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Instrutores</Label>
            <div className="space-y-1.5 max-h-56 overflow-y-auto mt-1.5">
              {instrutores.length ? instrutores.map((i) => {
                const checked = eqInstr.includes(i.id);
                return (
                  <label key={i.id} data-testid={`eq-instrutor-${i.id}`} className={`w-full flex items-center gap-2.5 border rounded-md p-2.5 cursor-pointer ${checked ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleEqInstr(i.id)} className="accent-[#49D7DC]" />
                    <span className="text-sm truncate">{i.display}</span>
                  </label>
                );
              }) : <p className="text-sm text-muted-foreground py-2">Nenhum instrutor ativo cadastrado.</p>}
            </div>
          </div>
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Monitores (digitação manual)</Label>
              <Button type="button" size="sm" variant="secondary" data-testid="eq-add-monitor" onClick={() => setEqMon((m) => [...m, ""])}><Plus className="h-3.5 w-3.5 mr-1" />Monitor</Button>
            </div>
            <div className="space-y-1.5">
              {eqMon.map((m, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input data-testid={`eq-monitor-input-${idx}`} value={m} onChange={(e) => setEqMon((arr) => arr.map((x, i) => (i === idx ? e.target.value : x)))} placeholder="Ex.: 3º SGT Silva" />
                  <Button type="button" size="icon" variant="ghost" data-testid={`eq-remover-monitor-${idx}`} onClick={() => setEqMon((arr) => arr.filter((_, i) => i !== idx))} className="shrink-0"><X className="h-4 w-4" /></Button>
                </div>
              ))}
              {!eqMon.length && <p className="text-xs text-muted-foreground">Nenhum monitor adicionado.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEquipeOpen(false)}>Cancelar</Button>
            <Button data-testid="eq-salvar" onClick={salvarEquipe} disabled={savingEq}>{savingEq ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Salvar equipe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={existOpen} onOpenChange={setExistOpen}>
        <DialogContent className="bg-popover border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Adicionar aluno existente</DialogTitle>
            <DialogDescription>Selecione um ou mais alunos já cadastrados para vincular a esta turma (o cadastro original é preservado).</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input data-testid="wizard-busca-existente" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome, nome de guerra, RG, matrícula, instituição, unidade..." className="pl-8" />
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {filtradosExist.map((a) => {
              const linked = jaNaTurma(a);
              const checked = existSel.includes(a.id);
              return (
                <button key={a.id} data-testid={`wizard-vincular-${a.id}`} disabled={linked} onClick={() => toggleExist(a.id)}
                  className={`w-full flex items-center justify-between gap-2 border rounded-md p-2.5 text-left ${linked ? "opacity-50 cursor-not-allowed border-border" : checked ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.nome}{a.nome_guerra ? <span className="text-xs text-primary font-mono ml-1">"{a.nome_guerra}"</span> : ""}</div>
                    <div className="text-xs text-muted-foreground truncate">{[a.matricula, a.instituicao_nome, a.unidade_nome].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  {linked
                    ? <span className="text-xs text-emerald-400 shrink-0 flex items-center gap-1"><Check className="h-3.5 w-3.5" />Já adicionado</span>
                    : checked ? <Check className="h-4 w-4 text-primary shrink-0" /> : <Plus className="h-4 w-4 text-muted-foreground shrink-0" />}
                </button>
              );
            })}
            {!filtradosExist.length && <p className="text-sm text-muted-foreground py-4 text-center">Nenhum aluno encontrado.</p>}
          </div>
          <Button data-testid="wizard-adicionar-selecionados" onClick={vincularExistentes} disabled={!existSel.length}>
            Adicionar {existSel.length || ""} selecionado(s) à turma
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={novaTurma} onOpenChange={closeNovaTurma}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Nova aplicação da instrução</DialogTitle>
            <DialogDescription>Crie uma nova aplicação desta instrução e selecione o instrutor responsável.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome da turma / aplicação</Label><Input data-testid="wt-nome" value={tForm.nome} onChange={(e) => setTForm({ ...tForm, nome: e.target.value })} placeholder="Ex.: Primeira Intervenção — 27º BPM" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Instituição</Label><Input data-testid="wt-instituicao" value={tForm.instituicao} onChange={(e) => setTForm({ ...tForm, instituicao: e.target.value })} placeholder="Ex.: PMERJ, Marinha, Exército" /></div>
              <div><Label>Unidade / Batalhão</Label><Input data-testid="wt-unidade" value={tForm.unidade} onChange={(e) => setTForm({ ...tForm, unidade: e.target.value })} placeholder="Ex.: 27º BPM, BOPE, CPRV" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Local</Label><Input data-testid="wt-local" value={tForm.local} onChange={(e) => setTForm({ ...tForm, local: e.target.value })} /></div>
              <div>
                <Label>Responsável</Label>
                <Popover open={respOpen} onOpenChange={setRespOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" role="combobox" data-testid="wt-responsavel" className="w-full justify-between font-normal">
                      <span className="truncate">{tForm.responsavel || "Selecione o instrutor"}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 bg-popover border-border w-[var(--radix-popover-trigger-width)] max-w-[90vw]" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar instrutor..." data-testid="wt-responsavel-search" />
                      <CommandList>
                        <CommandEmpty>Nenhum instrutor ativo encontrado.</CommandEmpty>
                        <CommandGroup>
                          {instrutores.map((i) => (
                            <CommandItem key={i.id} value={i.display} data-testid={`wt-responsavel-opt-${i.id}`}
                              onSelect={() => { setTForm({ ...tForm, responsavel_id: i.id, responsavel: i.display }); setRespOpen(false); }}>
                              <Check className={cn("mr-2 h-4 w-4", tForm.responsavel_id === i.id ? "opacity-100" : "opacity-0")} />
                              {i.display}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div><Label>Data</Label><Input type="date" data-testid="wt-data" value={tForm.data_inicio} onChange={(e) => setTForm({ ...tForm, data_inicio: e.target.value })} /></div>
            <div><Label>Observações</Label><Textarea data-testid="wt-obs" value={tForm.observacoes} onChange={(e) => setTForm({ ...tForm, observacoes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setNovaTurma(false)}>Cancelar</Button>
            <Button data-testid="wt-salvar" onClick={criarTurma} disabled={savingT}>{savingT ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Criar e continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SelGrid({ items, icon: Icon, color, onPick, testid, empty, sub }) {
  if (!items.length) return <p className="text-muted-foreground text-sm">{empty}</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((it) => (
        <button key={it.id} data-testid={`pick-${testid}-${it.id}`} onClick={() => onPick(it)}
          className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary hover:bg-card/70 transition-all text-left active:scale-[0.98]">
          <div className="p-2 rounded-lg bg-secondary"><Icon className={`h-5 w-5 ${color}`} /></div>
          <div className="min-w-0">
            <div className="font-semibold truncate">{it.nome}</div>
            {sub && <div className="text-xs text-muted-foreground">{sub(it)}</div>}
          </div>
          <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
        </button>
      ))}
    </div>
  );
}
